import { Box, IconButton, styled, SxProps, Theme, useTheme } from "@mui/material";
import { getFlexStyles, getScreenFluidBoxStyles } from "../theme/mixins";
import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { ModalProvider } from "./modal/ModalProvider";

export type BottomSheetProps = {
    onClose: () => void;
    bodyBuilder: (handleClose: () => void) => React.ReactNode;
    primaryButtonBuilder?: (handleClose: () => void) => React.ReactNode;
    viewMode?: "sheet" | "modal";
    modalSx?: SxProps<Theme>
};

export const BottomSheet = (props: BottomSheetProps) => {
    const { openModal } = ModalProvider.useModal();

    const { onClose, primaryButtonBuilder } = props;

    const viewMode = props.viewMode || "sheet";

    const [isVisible, setIsVisible] = useState(false);

    const theme = useTheme();

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        if (viewMode === "sheet") {
            setIsVisible(false);
            setTimeout(onClose, 300);
        }
    };

    useEffect(() => {
        if (viewMode === "modal") {
            openModal({
                children: (closeModal) => <Box sx={{ ...getFlexStyles("column"), gap: 2, width: "100%" }}>
                    {props.bodyBuilder(closeModal)}
                    <span></span>
                    {!!primaryButtonBuilder && primaryButtonBuilder(closeModal)}
                </Box>,
                sx: props.modalSx,
            }, onClose);
        }
    }, []);

    return (
        <>
            {viewMode === "sheet" && <>
                <StyleBottomSheetOverlay
                    className={isVisible ? 'visible' : ''}
                    onClick={handleClose}
                />

                <StyledBottomSheetWrapper className={isVisible ? 'visible' : ''}>
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                        <IconButton onClick={handleClose}>
                            <IoClose color={theme.palette.common.black} size={24} />
                        </IconButton>
                    </Box>

                    <StyledBottomSheetBody>
                        {(props.bodyBuilder(handleClose))}
                    </StyledBottomSheetBody>

                    {!!primaryButtonBuilder && primaryButtonBuilder(handleClose)}
                </StyledBottomSheetWrapper>
            </>}
        </>
    );
};

export const StyledBottomSheetWrapper = styled('div')(({ theme }: { theme: Theme }) => ({
    zIndex: theme.zIndex.drawer * 2 + 1,
    backgroundColor: theme.palette.background.default,
    position: "fixed",
    bottom: 0,
    left: 0,
    width: '100vw',
    maxHeight: '90vh',
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    borderTopLeftRadius: `${theme.shape.borderRadius * 2}px`,
    borderTopRightRadius: `${theme.shape.borderRadius * 2}px`,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(2),
    transform: 'translateY(100%)',
    transition: `all 300ms ${theme.transitions.easing.sharp}`,
    '&.visible': {
        transform: 'translateY(0)',
    }
}));

export const StyledBottomSheetBody = styled('div')(({ theme }: { theme: Theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflowY: 'auto',
    overflowX: 'hidden',
    flexGrow: 1,
}));

export const StyleBottomSheetOverlay = styled('div')(({ theme }: { theme: Theme }) => ({
    zIndex: theme.zIndex.drawer * 2,
    backgroundColor: theme.palette.common.black,
    ...getScreenFluidBoxStyles(),
    position: "fixed",
    top: 0,
    left: 0,
    opacity: 0,
    transition: `opacity 300ms ${theme.transitions.easing.sharp}`,
    '&.visible': {
        opacity: 0.45,
    }
}));